<script>
import { mapState } from "vuex";
import moment from "moment";
import DatePicker from "vue2-datepicker";

import Layout from "../../layouts/main";
import Stat from "@/components/widgets/stat";
import appConfig from "@/app.config";
import { distribucion } from "@/services/metricas";
import { etiquetaTipo } from "@/services/usuarios";
import { DIAS_POR_VENCER } from "@/services/suscripciones";
import { guardarSnapshotSiFalta, leerSnapshots } from "@/services/snapshotDiario";

/** Presets del filtro de fechas. `dias: null` significa "todo el histórico". */
const PRESETS = [
  { clave: "hoy", texto: "Hoy", dias: 0 },
  { clave: "7d", texto: "7 días", dias: 7 },
  { clave: "30d", texto: "30 días", dias: 30 },
  { clave: "mes", texto: "Este mes", dias: null },
  { clave: "todo", texto: "Todo", dias: null },
];

export default {
  page: {
    title: "Inicio",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, Stat, DatePicker },
  data() {
    return {
      presets: PRESETS,
      preset: "30d",
      rangoPersonalizado: null,
      granularidad: "day",
      snapshots: [],
      diasPorVencer: DIAS_POR_VENCER,
    };
  },
  computed: {
    ...mapState("analytics", ["cargando", "error", "cargadoEn"]),

    /** Rango activo, en forma { desde, hasta } de moment. */
    rango() {
      if (this.preset === "personalizado" && this.rangoPersonalizado) {
        const [desde, hasta] = this.rangoPersonalizado;
        if (desde && hasta) {
          return { desde: moment(desde).startOf("day"), hasta: moment(hasta).endOf("day") };
        }
      }
      const hasta = moment().endOf("day");
      if (this.preset === "hoy") return { desde: moment().startOf("day"), hasta };
      if (this.preset === "7d") return { desde: moment().subtract(6, "days").startOf("day"), hasta };
      if (this.preset === "30d") return { desde: moment().subtract(29, "days").startOf("day"), hasta };
      if (this.preset === "mes") return { desde: moment().startOf("month"), hasta };
      return null; // "todo": sin filtro
    },

    etiquetaRango() {
      if (!this.rango) return "todo el histórico";
      return `${this.rango.desde.format("DD/MM/YYYY")} – ${this.rango.hasta.format("DD/MM/YYYY")}`;
    },

    resumen() {
      return this.$store.getters["analytics/resumen"](this.rango);
    },

    // ===== Gráficas =====

    serieRegistros() {
      const { categorias, valores } = this.$store.getters["analytics/serieRegistros"](
        this.rango,
        this.granularidad
      );
      return {
        series: [{ name: "Registros", data: valores }],
        opciones: {
          chart: { type: "area", toolbar: { show: false } },
          dataLabels: { enabled: false },
          stroke: { curve: "smooth", width: 2 },
          colors: ["#556ee6"],
          xaxis: { categories: categorias },
          noData: { text: "Sin registros en este periodo" },
        },
      };
    },

    embudo() {
      const etapas = this.$store.getters["analytics/embudo"](this.rango);
      const base = etapas[0].valor || 1;
      return {
        etapas: etapas.map((e) => ({ ...e, porcentaje: Math.round((e.valor / base) * 100) })),
        series: [{ name: "Usuarios", data: etapas.map((e) => e.valor) }],
        opciones: {
          chart: { type: "bar", toolbar: { show: false } },
          plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 4 } },
          colors: ["#556ee6", "#50a5f1", "#34c38f"],
          dataLabels: { enabled: true },
          legend: { show: false },
          xaxis: { categories: etapas.map((e) => e.etapa) },
        },
      };
    },

    usuariosDelRango() {
      const rango = this.rango;
      if (!rango) return this.$store.state.analytics.usuarios;
      return this.$store.state.analytics.usuarios.filter(
        (u) =>
          u.creadoEn &&
          u.creadoEn.isValid() &&
          !u.creadoEn.isBefore(rango.desde) &&
          !u.creadoEn.isAfter(rango.hasta)
      );
    },

    porTipo() {
      const d = distribucion(this.usuariosDelRango, "tipo", {
        etiquetas: {
          usuario: etiquetaTipo("usuario"),
          comercio: etiquetaTipo("comercio"),
          sin_definir: etiquetaTipo("sin_definir"),
        },
      });
      return this.dona(d, ["#556ee6", "#f1b44c", "#adb5bd"]);
    },

    porMetodo() {
      const d = distribucion(this.usuariosDelRango, "metodoAlta", {
        etiquetas: { email: "Correo", google: "Google", sin_definir: "Sin definir" },
      });
      return this.dona(d, ["#34c38f", "#f46a6a", "#adb5bd"]);
    },

    porCiudad() {
      const d = distribucion(this.usuariosDelRango, "ciudad", { limite: 10 });
      return {
        series: [{ name: "Registros", data: d.valores }],
        opciones: {
          chart: { type: "bar", toolbar: { show: false } },
          plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
          colors: ["#50a5f1"],
          dataLabels: { enabled: false },
          xaxis: { categories: d.categorias },
          noData: { text: "Sin datos" },
        },
      };
    },

    porGiro() {
      const d = distribucion(this.$store.state.analytics.negocios, "giro", { limite: 8 });
      return this.dona(d, ["#556ee6", "#34c38f", "#f1b44c", "#f46a6a", "#50a5f1", "#74788d", "#e83e8c", "#adb5bd"]);
    },

    /** Evolución de suscriptores según los snapshots diarios acumulados. */
    evolucionSuscripciones() {
      return {
        series: [
          {
            name: "Suscriptores activos",
            data: this.snapshots.map((s) => s.suscriptores_activos || 0),
          },
          {
            name: "Registros totales",
            data: this.snapshots.map((s) => s.registros_total || 0),
          },
        ],
        opciones: {
          chart: { type: "line", toolbar: { show: false } },
          stroke: { curve: "smooth", width: 2 },
          colors: ["#34c38f", "#556ee6"],
          dataLabels: { enabled: false },
          xaxis: {
            categories: this.snapshots.map((s) => moment(s.fecha, "YYYY-MM-DD").format("DD/MM")),
          },
          noData: { text: "El histórico empieza a llenarse desde hoy" },
        },
      };
    },

    // ===== Tablas accionables =====

    porVencer() {
      return this.$store.getters["analytics/porVencer"](DIAS_POR_VENCER).slice(0, 10);
    },
    estancados() {
      return this.$store.getters["analytics/estancados"](7).slice(0, 10);
    },
    topReferidores() {
      return this.$store.getters["analytics/topReferidores"](10);
    },
  },
  methods: {
    /** Opciones comunes de las gráficas de dona. */
    dona({ categorias, valores }, colors) {
      return {
        series: valores,
        opciones: {
          chart: { type: "donut" },
          labels: categorias,
          colors,
          legend: { position: "bottom" },
          dataLabels: { enabled: true },
          noData: { text: "Sin datos" },
        },
      };
    },

    seleccionarPreset(clave) {
      this.preset = clave;
      this.rangoPersonalizado = null;
    },

    onRangoPersonalizado(valor) {
      this.rangoPersonalizado = valor;
      this.preset = valor ? "personalizado" : "30d";
    },

    async refrescar() {
      await this.$store.dispatch("analytics/refrescar");
      await this.registrarSnapshot();
    },

    /**
     * Deja la marca del día y recarga el histórico. Es idempotente: sólo escribe
     * si el documento de hoy aún no existe.
     */
    async registrarSnapshot() {
      const resumenGlobal = this.$store.getters["analytics/resumen"]({
        desde: moment().startOf("day"),
        hasta: moment().endOf("day"),
      });
      await guardarSnapshotSiFalta(resumenGlobal);
      this.snapshots = await leerSnapshots(90);
    },

    formatoMoneda(valor) {
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
      }).format(valor || 0);
    },
  },
  async mounted() {
    await this.$store.dispatch("analytics/cargar");
    await this.registrarSnapshot();
  },
};
</script>

<template>
  <Layout>
    <div class="mb-4">
      <img :src="require('@/assets/images/recnet-header.png')" alt="Recnet"
        class="img-fluid w-100 rounded shadow-sm"
        style="max-height: 260px; object-fit: cover; object-position: center;" />
    </div>

    <!-- Filtros -->
    <div class="card mb-3">
      <div class="card-body py-3">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="d-flex flex-wrap align-items-center">
            <b-button-group size="sm" class="mr-2 mb-2 mb-md-0">
              <b-button v-for="p in presets" :key="p.clave"
                :variant="preset === p.clave ? 'primary' : 'outline-primary'"
                @click="seleccionarPreset(p.clave)">{{ p.texto }}</b-button>
            </b-button-group>

            <date-picker :value="rangoPersonalizado" range format="DD/MM/YYYY" placeholder="Rango personalizado"
              class="mr-2 mb-2 mb-md-0" @input="onRangoPersonalizado" />

            <b-form-select v-model="granularidad" size="sm" class="w-auto mb-2 mb-md-0" :options="[
              { value: 'day', text: 'Por día' },
              { value: 'week', text: 'Por semana' },
              { value: 'month', text: 'Por mes' },
            ]" />
          </div>

          <div class="d-flex align-items-center">
            <small class="text-muted mr-3">Periodo: {{ etiquetaRango }}</small>
            <b-button size="sm" variant="outline-secondary" :disabled="cargando" @click="refrescar">
              <i class="mdi mdi-refresh"></i> {{ cargando ? 'Cargando…' : 'Actualizar' }}
            </b-button>
          </div>
        </div>

        <b-alert :show="!!error" variant="warning" class="mb-0 mt-3">{{ error }}</b-alert>
      </div>
    </div>

    <!-- Indicadores -->
    <b-row>
      <b-col md="6" xl="3">
        <Stat title="Registros en el periodo" :value="resumen.registrosPeriodo"
          :hint="`${resumen.registrosTotal} en total`" :variacion="resumen.registrosVariacion"
          icon="mdi mdi-account-plus" variant="primary" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Perfiles completados" :value="resumen.perfilesCompletados"
          :hint="`${resumen.tasaCompletitud}% de los registros`" icon="mdi mdi-account-check" variant="info" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Suscriptores activos" :value="resumen.suscriptoresActivos"
          :hint="`${resumen.tasaConversion}% de conversión`" icon="mdi mdi-star-circle" variant="success" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Ingreso mensual estimado" :value="formatoMoneda(resumen.mrrEstimado)"
          hint="Activos × precio de lista" icon="mdi mdi-cash-multiple" variant="warning" />
      </b-col>
    </b-row>

    <b-row>
      <b-col md="6" xl="3">
        <Stat title="Negocios" :value="resumen.negociosTotal" :hint="`${resumen.negociosPeriodo} en el periodo`"
          icon="mdi mdi-storefront" variant="primary" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Sesiones" :value="resumen.sesionesTotal" icon="mdi mdi-video" variant="info" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Vacantes activas" :value="resumen.vacantesActivas" icon="mdi mdi-briefcase" variant="success" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Eventos activos" :value="resumen.eventosActivos" icon="mdi mdi-calendar-star" variant="warning" />
      </b-col>
    </b-row>

    <!-- Registros y embudo -->
    <b-row>
      <b-col lg="8">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Registros en el tiempo</h4>
            <apexchart type="area" height="300" :series="serieRegistros.series"
              :options="serieRegistros.opciones" />
          </div>
        </div>
      </b-col>
      <b-col lg="4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Embudo de alta</h4>
            <apexchart type="bar" height="300" :series="embudo.series" :options="embudo.opciones" />
            <ul class="list-unstyled mb-0 mt-2">
              <li v-for="e in embudo.etapas" :key="e.etapa" class="d-flex justify-content-between small">
                <span class="text-muted">{{ e.etapa }}</span>
                <span class="font-weight-bold">{{ e.valor }} ({{ e.porcentaje }}%)</span>
              </li>
            </ul>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Composición -->
    <b-row>
      <b-col lg="4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Usuarios vs. comercios</h4>
            <apexchart type="donut" height="280" :series="porTipo.series" :options="porTipo.opciones" />
          </div>
        </div>
      </b-col>
      <b-col lg="4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Método de alta</h4>
            <apexchart type="donut" height="280" :series="porMetodo.series" :options="porMetodo.opciones" />
          </div>
        </div>
      </b-col>
      <b-col lg="4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Negocios por giro</h4>
            <apexchart type="donut" height="280" :series="porGiro.series" :options="porGiro.opciones" />
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Altas por ciudad</h4>
            <apexchart type="bar" height="320" :series="porCiudad.series" :options="porCiudad.opciones" />
          </div>
        </div>
      </b-col>
      <b-col lg="6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-1">Evolución de suscripciones</h4>
            <p class="text-muted small mb-3">
              Construida con la marca diaria que deja el tablero; Stripe sólo entrega el estado actual.
            </p>
            <apexchart type="line" height="290" :series="evolucionSuscripciones.series"
              :options="evolucionSuscripciones.opciones" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Tablas accionables -->
    <b-row>
      <b-col lg="6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-1">Vigencias por vencer</h4>
            <p class="text-muted small mb-3">Próximos {{ diasPorVencer }} días</p>
            <b-table small responsive hover :items="porVencer" :fields="[
              { key: 'name', label: 'Nombre' },
              { key: 'phone', label: 'Teléfono' },
              { key: 'vigencia', label: 'Vence' },
              { key: 'diasRestantes', label: 'Días', class: 'text-center' },
            ]" show-empty empty-text="Nadie vence en este plazo.">
              <template #cell(diasRestantes)="{ item }">
                <b-badge :variant="item.diasRestantes <= 7 ? 'danger' : 'warning'">{{ item.diasRestantes }}</b-badge>
              </template>
            </b-table>
          </div>
        </div>
      </b-col>
      <b-col lg="6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-1">Registros sin completar</h4>
            <p class="text-muted small mb-3">Se registraron hace más de 7 días y no terminaron su perfil</p>
            <b-table small responsive hover :items="estancados" :fields="[
              { key: 'email', label: 'Correo' },
              { key: 'creadoEnFormatted', label: 'Registro' },
              { key: 'diasSinCompletar', label: 'Días', class: 'text-center' },
            ]" show-empty empty-text="Sin registros estancados." />
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-1">Mejores referidores</h4>
            <p class="text-muted small mb-3">Ordenados por referidos que sí pagan membresía</p>
            <b-table small responsive hover :items="topReferidores" :fields="[
              { key: 'name', label: 'Nombre' },
              { key: 'email', label: 'Correo' },
              { key: 'referral_code', label: 'Código' },
              { key: 'referidosTotal', label: 'Referidos', class: 'text-center' },
              { key: 'referidosPlus', label: 'Con Plus', class: 'text-center' },
              { key: 'tasa', label: 'Conversión', class: 'text-center' },
            ]" show-empty empty-text="Todavía no hay referidos registrados.">
              <template #cell(referidosPlus)="{ item }">
                <b-badge variant="success">{{ item.referidosPlus }}</b-badge>
              </template>
              <template #cell(tasa)="{ item }">{{ item.tasa }}%</template>
            </b-table>
          </div>
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
